import React, { useState, useEffect, useContext, useRef, useCallback, useMemo } from 'react';
import WaveSurfer from 'wavesurfer.js';
import { ProjectContext } from '../context/project_context';

const MIN_SEG = 0.1;

/**
 * Compute resulting row A / B ranges for a split time that may be
 * inside the row, before start, or after end.
 */
function computeSplitRanges(startTime, endTime, splitTime) {
  if (splitTime < startTime) {
    return {
      rowA: { start: splitTime, end: startTime },
      rowB: { start: startTime, end: endTime },
      position: 'before',
    };
  }
  if (splitTime > endTime) {
    return {
      rowA: { start: startTime, end: endTime },
      rowB: { start: endTime, end: splitTime },
      position: 'after',
    };
  }
  return {
    rowA: { start: startTime, end: splitTime },
    rowB: { start: splitTime, end: endTime },
    position: 'inside',
  };
}

function isSplitTimeValid(startTime, endTime, splitTime) {
  if (splitTime < startTime) {
    return startTime - splitTime >= MIN_SEG;
  }
  if (splitTime > endTime) {
    return splitTime - endTime >= MIN_SEG;
  }
  return splitTime - startTime >= MIN_SEG && endTime - splitTime >= MIN_SEG;
}

/**
 * SplitRowModal - Split a transcript row at a waveform time + text caret
 *
 * Props:
 * - isOpen: boolean
 * - onClose: function
 * - onSplit: function(splitTime, firstText, secondText)
 * - transcript: english transcript row { id, original_text, start_time, end_time }
 */
const SplitRowModal = ({
  isOpen,
  onClose,
  onSplit,
  transcript = null,
}) => {
  const {
    formatTimeCode,
    project,
    mediaRef,
    setIsPlaying,
  } = useContext(ProjectContext);

  const waveformRef = useRef(null);
  const wavesurferRef = useRef(null);
  const textRef = useRef(null);
  const mediaFallbackRef = useRef(null);
  const segmentScrollRef = useRef(0);

  const [splitTime, setSplitTime] = useState(0);
  const [splitIndex, setSplitIndex] = useState(0);
  const [isPlayingLocal, setIsPlayingLocal] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [loadError, setLoadError] = useState('');
  const [useFallback, setUseFallback] = useState(false);
  const [mediaDuration, setMediaDuration] = useState(0);

  const startTime = transcript ? parseFloat(transcript.start_time) : 0;
  const endTime = transcript ? parseFloat(transcript.end_time) : 0;
  const originalText = transcript?.original_text || '';
  const mediaUrl = project?.audio_url || project?.video_url || project?.media_url;
  const isVideoMedia = !project?.audio_url && !!project?.video_url;

  const clampToMedia = useCallback((time) => {
    const max = mediaDuration > 0 ? mediaDuration : Math.max(endTime + 60, time);
    return Math.min(Math.max(time, 0), max);
  }, [mediaDuration, endTime]);

  const keepWaveformFocused = useCallback(() => {
    const ws = wavesurferRef.current;
    if (!ws || typeof ws.setScrollTime !== 'function') return;
    ws.setScrollTime(segmentScrollRef.current);
  }, []);

  const updateSplitIndexFromCaret = useCallback(() => {
    const el = textRef.current;
    if (!el) return;
    setSplitIndex(el.selectionStart ?? 0);
  }, []);

  // Pause floating player when modal opens; reset local state
  useEffect(() => {
    if (!isOpen || !transcript) return;

    if (mediaRef?.current && !mediaRef.current.paused) {
      mediaRef.current.pause();
      setIsPlaying?.(false);
    }

    const mid = (startTime + endTime) / 2;
    setSplitTime(mid);
    setSplitIndex(Math.floor(originalText.length / 2));
    setIsPlayingLocal(false);
    setIsReady(false);
    setIsSubmitting(false);
    setError('');
    setLoadError('');
    setUseFallback(false);
    setMediaDuration(0);
  }, [isOpen, transcript?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Init / destroy wavesurfer
  useEffect(() => {
    if (!isOpen || !transcript || !waveformRef.current || useFallback) return undefined;

    if (!mediaUrl) {
      setLoadError('No project audio or video URL available. Upload media before splitting.');
      return undefined;
    }

    let destroyed = false;
    const ws = WaveSurfer.create({
      container: waveformRef.current,
      url: mediaUrl,
      waveColor: '#94a3b8',
      progressColor: '#0d6efd',
      cursorColor: '#dc3545',
      cursorWidth: 2,
      height: 96,
      barWidth: 2,
      barGap: 1,
      barRadius: 1,
      normalize: true,
      dragToSeek: true,
      interact: true,
      autoScroll: false,
      autoCenter: false,
    });

    wavesurferRef.current = ws;

    const onReady = () => {
      if (destroyed) return;
      setIsReady(true);
      setLoadError('');
      setMediaDuration(ws.getDuration() || 0);

      const segmentDuration = Math.max(endTime - startTime, 0.2);
      const width = waveformRef.current?.clientWidth || 600;
      const pxPerSec = (width * 0.85) / segmentDuration;
      try {
        ws.zoom(Math.max(pxPerSec, 20));
      } catch (_) {
        // zoom may fail on very short clips
      }

      const mid = (startTime + endTime) / 2;
      const scrollAt = Math.max(0, startTime - segmentDuration * 0.05);
      segmentScrollRef.current = scrollAt;
      ws.setTime(mid);
      setSplitTime(mid);
      if (typeof ws.setScrollTime === 'function') {
        ws.setScrollTime(scrollAt);
      }
    };

    const onTimeUpdate = (time) => {
      if (destroyed) return;
      setSplitTime(time);
      // Keep view locked on the original row segment while playhead may leave it
      keepWaveformFocused();
    };

    const onInteraction = (time) => {
      if (destroyed) return;
      setSplitTime(time);
      keepWaveformFocused();
    };

    const onPlay = () => setIsPlayingLocal(true);
    const onPause = () => setIsPlayingLocal(false);
    const onFinish = () => setIsPlayingLocal(false);
    const onError = () => {
      if (destroyed) return;
      // S3 often lacks CORS for fetch/decode — fall back to HTML5 scrubber
      setUseFallback(true);
      setIsReady(true);
      setLoadError('');
      try {
        ws.destroy();
      } catch (_) {
        // ignore
      }
      wavesurferRef.current = null;
    };

    ws.on('ready', onReady);
    ws.on('timeupdate', onTimeUpdate);
    ws.on('interaction', onInteraction);
    ws.on('play', onPlay);
    ws.on('pause', onPause);
    ws.on('finish', onFinish);
    ws.on('error', onError);

    return () => {
      destroyed = true;
      try {
        ws.destroy();
      } catch (_) {
        // ignore
      }
      wavesurferRef.current = null;
    };
  }, [isOpen, transcript?.id, mediaUrl, startTime, endTime, useFallback, keepWaveformFocused]);

  // Fallback HTML5 scrubbing when wavesurfer cannot decode (e.g. S3 CORS)
  useEffect(() => {
    if (!isOpen || !useFallback || !mediaUrl) return undefined;
    const media = mediaFallbackRef.current;
    if (!media) return undefined;

    const mid = (startTime + endTime) / 2;
    media.src = mediaUrl;
    const onLoaded = () => {
      const duration = media.duration || 0;
      setMediaDuration(duration);
      media.currentTime = mid;
      setSplitTime(mid);
      setIsReady(true);
    };
    const onTimeUpdate = () => {
      setSplitTime(media.currentTime);
    };
    const onPlay = () => setIsPlayingLocal(true);
    const onPause = () => setIsPlayingLocal(false);

    media.addEventListener('loadedmetadata', onLoaded);
    media.addEventListener('timeupdate', onTimeUpdate);
    media.addEventListener('play', onPlay);
    media.addEventListener('pause', onPause);
    media.load();

    return () => {
      media.pause();
      media.removeEventListener('loadedmetadata', onLoaded);
      media.removeEventListener('timeupdate', onTimeUpdate);
      media.removeEventListener('play', onPlay);
      media.removeEventListener('pause', onPause);
    };
  }, [isOpen, useFallback, mediaUrl, startTime, endTime]);

  // Place caret at default split index once textarea mounts
  useEffect(() => {
    if (!isOpen || !textRef.current) return;
    const el = textRef.current;
    const idx = Math.floor(originalText.length / 2);
    el.focus();
    el.setSelectionRange(idx, idx);
    setSplitIndex(idx);
  }, [isOpen, transcript?.id, originalText.length]);

  const seekLocal = (time) => {
    const next = clampToMedia(time);
    setSplitTime(next);
    const ws = wavesurferRef.current;
    if (ws) {
      ws.setTime(next);
      keepWaveformFocused();
      return;
    }
    const media = mediaFallbackRef.current;
    if (media) media.currentTime = next;
  };

  const handlePlayPause = useCallback(() => {
    const ws = wavesurferRef.current;
    if (ws && isReady) {
      if (ws.isPlaying()) {
        ws.pause();
      } else {
        ws.play();
      }
      return;
    }

    const media = mediaFallbackRef.current;
    if (!media || !isReady) return;
    if (!media.paused) {
      media.pause();
    } else {
      media.play();
    }
  }, [isReady]);

  // Space to play/pause while modal is open
  useEffect(() => {
    if (!isOpen) return undefined;

    const onKeyDown = (e) => {
      if (e.code !== 'Space' && e.key !== ' ') return;
      // Allow Shift+Space to remain for FloatingMediaPlayer
      if (e.shiftKey || e.ctrlKey || e.metaKey || e.altKey) return;
      if (isSubmitting) return;

      e.preventDefault();
      e.stopPropagation();
      handlePlayPause();
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [isOpen, isSubmitting, handlePlayPause]);

  const handleNudge = (delta) => {
    if (!isReady) return;
    const current = wavesurferRef.current
      ? wavesurferRef.current.getCurrentTime()
      : (mediaFallbackRef.current?.currentTime ?? splitTime);
    seekLocal(current + delta);
  };

  const firstText = originalText.slice(0, splitIndex);
  const secondText = originalText.slice(splitIndex);

  const ranges = useMemo(
    () => computeSplitRanges(startTime, endTime, splitTime),
    [startTime, endTime, splitTime]
  );

  const splitTimeValid = isSplitTimeValid(startTime, endTime, splitTime);
  const firstTextValid = firstText.trim().length > 0;
  const canConfirm = isReady && splitTimeValid && firstTextValid && !isSubmitting && !loadError;

  const scrubMin = Math.max(0, startTime - 60);
  const scrubMax = mediaDuration > 0
    ? mediaDuration
    : Math.max(endTime + 60, splitTime + 1);

  const positionHint =
    ranges.position === 'before'
      ? 'Split is before the row — Row A will be inserted before the original range.'
      : ranges.position === 'after'
        ? 'Split is after the row — Row B will extend past the original end.'
        : 'Split is inside the row.';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canConfirm) {
      if (!firstTextValid) setError('Place the caret so the first half has text');
      else if (!splitTimeValid) setError('Split time must leave at least 0.1s on each resulting segment');
      return;
    }

    setIsSubmitting(true);
    setError('');
    try {
      const ws = wavesurferRef.current;
      if (ws?.isPlaying()) ws.pause();
      const media = mediaFallbackRef.current;
      if (media && !media.paused) media.pause();
      await onSplit(splitTime, firstText, secondText);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to split row');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    const ws = wavesurferRef.current;
    if (ws?.isPlaying()) ws.pause();
    const media = mediaFallbackRef.current;
    if (media && !media.paused) media.pause();
    onClose();
  };

  if (!isOpen || !transcript) return null;

  return (
    <div
      className="modal show d-block"
      tabIndex="-1"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      onClick={(e) => e.target === e.currentTarget && handleClose()}
    >
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              Split Row #{transcript.sequence_number}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={handleClose}
              disabled={isSubmitting}
            />
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {error && (
                <div className="alert alert-danger py-2 mb-3">{error}</div>
              )}
              {loadError && (
                <div className="alert alert-warning py-2 mb-3">{loadError}</div>
              )}

              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <label className="form-label mb-0">Audio (row segment focused)</label>
                  <small className="text-muted">
                    Row: {formatTimeCode(startTime)} → {formatTimeCode(endTime)}
                  </small>
                </div>
                <div
                  ref={waveformRef}
                  style={{
                    width: '100%',
                    background: '#f8f9fa',
                    borderRadius: 4,
                    border: '1px solid #dee2e6',
                    minHeight: useFallback ? 0 : 96,
                    display: useFallback ? 'none' : 'block',
                  }}
                />
                {useFallback && (
                  <div className="p-3 border rounded bg-light">
                    {isVideoMedia ? (
                      <video
                        ref={mediaFallbackRef}
                        preload="auto"
                        style={{ display: 'none' }}
                      />
                    ) : (
                      <audio ref={mediaFallbackRef} preload="auto" />
                    )}
                    <input
                      type="range"
                      className="form-range"
                      min={scrubMin}
                      max={scrubMax}
                      step={0.01}
                      value={Math.min(Math.max(splitTime, scrubMin), scrubMax)}
                      onChange={(e) => seekLocal(parseFloat(e.target.value))}
                    />
                    <small className="text-muted">
                      Waveform unavailable (CORS). Scrub beyond the row timecodes if needed
                      {isVideoMedia ? ' (using project video).' : '.'}
                    </small>
                  </div>
                )}
                <div className="d-flex align-items-center gap-2 mt-2 flex-wrap">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => handleNudge(-0.5)}
                    disabled={!isReady}
                    title="Nudge -0.5s"
                  >
                    −0.5s
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    onClick={handlePlayPause}
                    disabled={!isReady}
                  >
                    {isPlayingLocal ? 'Pause' : 'Play'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => handleNudge(0.5)}
                    disabled={!isReady}
                    title="Nudge +0.5s"
                  >
                    +0.5s
                  </button>
                  <span className="ms-2">
                    Split at:{' '}
                    <strong className={splitTimeValid ? 'text-danger' : 'text-muted'}>
                      {formatTimeCode(splitTime)}
                    </strong>
                  </span>
                  {!isReady && !loadError && (
                    <small className="text-muted ms-2">Loading waveform…</small>
                  )}
                </div>
                <small className="text-muted d-block mt-1">
                  Space to play/pause. Play and nudge can go past the row. Waveform stays focused on the row segment. {positionHint}
                </small>
              </div>

              <div className="mb-3">
                <label className="form-label">
                  Original Text — click to place the caret where the text should split
                </label>
                <textarea
                  ref={textRef}
                  className="form-control font-monospace"
                  value={originalText}
                  readOnly
                  rows={4}
                  onSelect={updateSplitIndexFromCaret}
                  onClick={updateSplitIndexFromCaret}
                  onKeyUp={updateSplitIndexFromCaret}
                  style={{ cursor: 'text' }}
                />
                <small className="text-muted">
                  Caret at character {splitIndex} of {originalText.length}
                </small>
              </div>

              <div className="row g-2">
                <div className="col-md-6">
                  <div className="border rounded p-2 bg-light h-100">
                    <small className="text-muted d-block">Row A (updated)</small>
                    <strong className="small">
                      {formatTimeCode(ranges.rowA.start)} → {formatTimeCode(ranges.rowA.end)}
                    </strong>
                    <div className="mt-1" style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}>
                      {firstText || <em className="text-danger">(empty)</em>}
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="border rounded p-2 bg-light h-100">
                    <small className="text-muted d-block">Row B (new)</small>
                    <strong className="small">
                      {formatTimeCode(ranges.rowB.start)} → {formatTimeCode(ranges.rowB.end)}
                    </strong>
                    <div className="mt-1" style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}>
                      {secondText || <em className="text-muted">(empty)</em>}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={!canConfirm}
              >
                {isSubmitting ? 'Splitting…' : 'Split Row'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SplitRowModal;
